import React from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { Navigate } from "react-router-dom";
import { RequireScope } from "src/components/RequireScope";
import { useMe } from "src/context/MeContext";
import { isRootlyBranded } from "src/utils/brandTheme";

import BillingSection from "../components/BillingSection";
import { useSettingsMobileHeader } from "../hooks/useSettingsMobileHeader";
import { BillingRouteQuery } from "./__generated__/BillingRouteQuery.graphql";

const formatUTCDate = (year: number, month: number, day: number, endOfDay = false): string => {
  const m = String(month).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  const time = endOfDay ? "23:59:59.999Z" : "00:00:00.000Z";
  return `${year}-${m}-${d}T${time}`;
};

const billingRouteQuery = graphql`
  query BillingRouteQuery($start: DateTime!, $end: DateTime!) {
    me {
      ...appMe
      organization {
        id
        name
        isSuspended
        suspensionReason
        suspendedAt
        organizationBilling {
          ...BillingSection_organizationBilling
        }
      }
    }
    analyticsByDay(start: $start, end: $end) {
      totalCredits
      dataPoints {
        day
        category
        credits
      }
    }
  }
`;

export default function BillingRoute() {
  const me = useMe();
  const brandTheme = me.organization?.brandTheme ?? "TIERZERO";
  if (isRootlyBranded(brandTheme)) {
    return <Navigate to="/settings" replace />;
  }
  return (
    <RequireScope scope="read:billing">
      <BillingRouteContent />
    </RequireScope>
  );
}

function BillingRouteContent() {
  useSettingsMobileHeader("Billing");
  const me = useMe();
  const now = new Date();
  const year = now.getUTCFullYear();

  // Query 2 years of data so periods with custom anchors (e.g. annual starting mid-year) are covered
  const data = useLazyLoadQuery<BillingRouteQuery>(billingRouteQuery, {
    start: formatUTCDate(year - 1, 1, 1),
    end: formatUTCDate(year, 12, 31, true),
  });

  const organization = data.me?.organization;
  const organizationBilling = organization?.organizationBilling;

  if (!organization) {
    return <div>Organization not found</div>;
  }

  if (!organizationBilling) {
    return <div>Billing information not available</div>;
  }

  return (
    <BillingSection
      organizationBilling={organizationBilling}
      brandTheme={me.organization?.brandTheme}
      isSuspended={organization.isSuspended}
      suspensionReason={organization.suspensionReason ?? null}
      suspendedAt={organization.suspendedAt ?? null}
      analyticsByDay={data.analyticsByDay}
    />
  );
}
